import React from 'react';
import moment from 'moment';
import BranchInfo from 'parcelshops/components/BranchInfo';

import DropOffCode from 'components/DropOffCode';
import { currentLanguage, weekDays } from 'helpers/utils';
import { Button, Icon } from 'ui';

type Props = {
  shipping: BackendPublic.Shipping;
};

function IconText({ iconName, text }: { iconName: UIIconName; text: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex flex-shrink-0">
        <Icon name={iconName} fitted color="gray" colorIntensity={600} solid />
      </div>
      <span className="text-sm text-gray-600">{text}</span>
    </div>
  );
}

export function getDayName(date: moment.Moment) {
  const today = moment().startOf('day');
  const tomorrow = moment(today).add(1, 'day');

  if (date.isSame(today)) return pgettext('portal_i18n', 'Today');
  if (date.isSame(tomorrow)) return pgettext('portal_i18n', 'Tomorrow');
  return weekDays[date.day() - 1 >= 0 ? date.day() - 1 : 6];
}

function ShippingDetail({ shipping }: Props) {
  const getFormattedPickupTime = () => {
    if (!shipping.pickup_interval) {
      return '';
    }

    const [date, start, end] = shipping.pickup_interval.split('_');
    const pickupDate = moment(date);
    const intervalStart = moment(start, 'HH:mm').format('H:mm');
    const intervalEnd = moment(end, 'HH:mm').format('H:mm');
    const day = `${getDayName(pickupDate)} ${pickupDate.format('D. M.')}`;

    return interpolate(
      gettext('%(day)s between %(interval_start)s and %(interval_end)s'),
      {
        day,
        interval_start: intervalStart,
        interval_end: intervalEnd,
      },
      true,
    );
  };

  const getFormattedShippingAddress = () => {
    if (!shipping.sender_address) {
      return '';
    }

    return `${shipping.sender_address.street} ${shipping.sender_address.house_number},
          ${shipping.sender_address.city} ${shipping.sender_address.postal_code}`;
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          {shipping.provider_logo_url && (
            <div>
              <img src={shipping.provider_logo_url} alt="shipping-provider-logo" className="h-10" />
            </div>
          )}
          <div>
            <div className="font-medium">{currentLanguage(shipping.option_name)}</div>
            {shipping.submit_type !== 'IN_PERSON' && (
              <div className="text-xs text-gray-500">{shipping.status_name}</div>
            )}
          </div>
        </div>
        {shipping.submit_type === 'PICKUP' && (
          <div className="flex flex-col gap-2">
            <IconText iconName="question-mark-circle" text={gettext('A courier picks up the package')} />
            <IconText iconName="calendar" text={getFormattedPickupTime()} />
            <IconText
              iconName="location-marker"
              text={
                <>
                  {gettext('From the address')} {getFormattedShippingAddress()}
                </>
              }
            />
          </div>
        )}
        {shipping.submit_type === 'IN_PERSON' && shipping.customer_parcel_shop && (
          <div className="flex flex-col gap-2">
            <IconText iconName="question-mark-circle" text={gettext('In-person return to branch')} />
            <BranchInfo branch={shipping.customer_parcel_shop} photoSize="xl" showOpeningHours showMapLink />
          </div>
        )}
      </div>
      <div className="inline-flex flex-wrap gap-4">
        {shipping.drop_off_code && <DropOffCode code={shipping.drop_off_code} />}
        <div className="flex flex-col gap-2">
          {shipping.labels.map(
            (label) =>
              label.file && (
                <a href={label.file} target="_blank" rel="noopener noreferrer">
                  <Button>{gettext('Download shipping label')}</Button>
                </a>
              ),
          )}
        </div>
        {shipping.tracking_url && (
          <a href={shipping.tracking_url} target="_blank" rel="noopener noreferrer">
            <Button>{gettext('Package tracking')}</Button>
          </a>
        )}
      </div>
    </div>
  );
}

export default ShippingDetail;
